import React from 'react';
import { Helmet } from 'react-helmet';
import BookingSection from '../components/BookingSection';
import { Car, MapPin, Clock, Star, Shield, Euro } from 'lucide-react';

const TaxiAnvers = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Taxi Anvers - Chauffeur Privé Luxe | Service VIP 24/7</title>
        <meta name="description" content="Service de taxi privé de luxe à Anvers. Chauffeur professionnel VIP, Mercedes & Lexus, disponible 24/7. Réservation en ligne instantanée. Prix fixe garanti." />
        <meta name="keywords" content="taxi anvers, chauffeur privé anvers, taxi luxe anvers, vtc anvers, transfert anvers, taxi antwerpen" />
        <link rel="canonical" href="https://www.luxurytransferservices.be/taxi-anvers" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gold via-yellow-300 to-gold bg-clip-text text-transparent">
              Taxi Anvers - Chauffeur Privé de Luxe
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Service VIP 24/7 à Anvers et ses environs. Véhicules premium Mercedes & Lexus. 
              Réservation instantanée, prix fixe garanti.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-gold" />
                <span>Service 5 étoiles</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gold" />
                <span>Disponible 24/7</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-gold" />
                <span>Assurance premium</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4">
          <BookingSection defaultPickup="Anvers" />
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gold">
            Nos Services à Anvers
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gray-900 p-6 rounded-lg border border-gold/20 hover:border-gold/50 transition">
              <MapPin className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-xl font-bold mb-3">Transfert Aéroport</h3>
              <p className="text-gray-400">
                Anvers ↔ Zaventem, Charleroi, Schiphol. Service porte-à-porte avec suivi de vol.
              </p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg border border-gold/20 hover:border-gold/50 transition">
              <Car className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-xl font-bold mb-3">Chauffeur à Disposition</h3>
              <p className="text-gray-400">
                Location avec chauffeur à l&apos;heure ou à la journée. Idéal pour visites et événements.
              </p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg border border-gold/20 hover:border-gold/50 transition">
              <Euro className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-xl font-bold mb-3">Prix Fixe Garanti</h3>
              <p className="text-gray-400">
                Tarif connu à l&apos;avance. Aucun supplément, aucune surprise. Paiement sécurisé en ligne.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-invert prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gold mb-6">Taxi Privé de Luxe à Anvers</h2>
            <p className="text-gray-300 mb-4">
              Besoin d&apos;un <strong>taxi à Anvers</strong> ? Notre service de <strong>chauffeur privé VIP</strong> vous 
              offre une expérience de transport premium dans la deuxième plus grande ville de Belgique. Que ce soit pour 
              un transfert vers l&apos;aéroport de Zaventem, un déplacement professionnel au port d&apos;Anvers, ou une sortie 
              dans le centre historique, nos chauffeurs professionnels sont à votre service 24/7.
            </p>

            <h3 className="text-2xl font-bold text-gold mb-4 mt-8">Pourquoi Choisir Notre Taxi à Anvers ?</h3>
            <ul className="text-gray-300 space-y-2 mb-6">
              <li>✅ Flotte premium : Mercedes E-Class, Lexus ES300h</li>
              <li>✅ Chauffeurs professionnels multilingues (FR/NL/EN)</li>
              <li>✅ Réservation en ligne instantanée 24/7</li>
              <li>✅ Prix fixe sans surprise ni supplément</li>
              <li>✅ Suivi de vol pour les transferts aéroport</li>
              <li>✅ Paiement sécurisé en ligne ou cash</li>
            </ul>

            <h3 className="text-2xl font-bold text-gold mb-4 mt-8">Trajets Populaires depuis Anvers</h3>
            <ul className="text-gray-300 space-y-2 mb-6">
              <li>🚗 Anvers → Aéroport Zaventem (45 min)</li>
              <li>🚗 Anvers → Bruxelles Centre (50 min)</li>
              <li>🚗 Anvers → Aéroport Charleroi (1h30)</li>
              <li>🚗 Anvers → Amsterdam Schiphol (1h40)</li>
              <li>🚗 Anvers → Gand (1h)</li>
              <li>🚗 Anvers → Port d&apos;Anvers (15 min)</li>
            </ul>

            <h3 className="text-2xl font-bold text-gold mb-4 mt-8">Service Premium pour Professionnels</h3>
            <p className="text-gray-300 mb-4">
              Notre <strong>service de taxi VIP à Anvers</strong> est parfait pour les déplacements d&apos;affaires. 
              Wifi à bord, chargeurs, bouteilles d&apos;eau, et surtout discrétion et ponctualité garanties. 
              Que vous ayez une réunion au Diamond District, au port, ou dans un des nombreux sièges sociaux 
              d&apos;Anvers, nous vous y emmenons en tout confort.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-gold/10 to-gold/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Réservez Votre Taxi à Anvers</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Réservation instantanée. Prix fixe. Service 24/7.
          </p>
          <a 
            href="#booking" 
            className="inline-block bg-gold text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-gold/90 transition"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Réserver Maintenant
          </a>
        </div>
      </section>
    </div>
  );
};

export default TaxiAnvers;
